import { Stripe, StripeElements } from '@stripe/stripe-js';
import { CreatePaymentMethodUsBankAccountData } from '@stripe/stripe-js';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { AddPaymentMethodMutation, AddPaymentMethodMutationVariables } from '@/graphql/paymentMethods';
import { GetSetupIntentQuery } from '@/graphql/setupIntent';

type GetSecret = (options?: {
  cancelRefetch?: boolean;
  throwOnError?: boolean;
}) => Promise<UseQueryResult<GetSetupIntentQuery>>;

export async function addPaymentMethod(
  mutation: UseMutationResult<AddPaymentMethodMutation, unknown, AddPaymentMethodMutationVariables>,
  getSecret: GetSecret,
  stripe: Stripe | null,
  elements: StripeElements | null,
  bank?: CreatePaymentMethodUsBankAccountData
) {
  const paymentToken = stripe && (await stripeToken(getSecret, stripe, elements, bank));
  if (!paymentToken) {
    throw new Error('No payment token was returned.');
  }

  return await mutation.mutateAsync({
    from: '', // From is used for guest giving. Should not be required, but typescript was complaining.
    paymentMethod: {
      paymentToken,
    },
  });
}

async function stripeToken(
  getSecret: GetSecret,
  stripe: Stripe,
  elements: StripeElements | null,
  bank?: CreatePaymentMethodUsBankAccountData
) {
  const { setupIntent, error } = (await getSetupIntent(getSecret, stripe, elements, bank)) ?? {};
  if (setupIntent) {
    return setupIntent.id;
  }
  if (error) {
    throw error;
  }
}

export async function getSetupIntent(
  getSecret: GetSecret,
  stripe: Stripe,
  elements?: StripeElements | null,
  bank?: CreatePaymentMethodUsBankAccountData
) {
  const { data, error } = await getSecret({ throwOnError: true });
  const secret = data?.setupIntent?.clientSecret;
  if (!secret) {
    error && console.error(error);
    throw new Error('Could not retrieve secret for setup intent.');
  }
  if (bank) {
    return await stripe?.confirmUsBankAccountSetup(secret, { payment_method: bank });
  }
  const element = elements?.getElement('card');
  if (!element) {
    throw new Error('Stripe Element was not found.');
  }
  return await stripe.confirmCardSetup(secret, {
    payment_method: {
      card: element,
    },
  });
}
